import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import ReactGA from "react-ga4";
import ShareTestCallCardStyled from './ShareTestCallCardStyled';
import { getCorsHeaders } from './utils/corsHeaders';
import { getUrlParams } from './utils/urlParams';

// Inline styled components using style objects
const styles = {
    container: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        background: 'linear-gradient(135deg, #0c0a1e 0%, #1a1144 25%, #2a1f5f 50%, #1a1144 75%, #0c0a1e 100%)',
        overflow: 'auto'
    },
    overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
            radial-gradient(circle at 20% 80%, rgba(79, 70, 229, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(99, 102, 241, 0.12) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(139, 92, 246, 0.08) 0%, transparent 50%)
        `,
        pointerEvents: 'none'
    },
    content: {
        position: 'relative',
        zIndex: 1,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
    },
    glassCard: {
        background: 'rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        padding: '40px',
        maxWidth: '900px',
        width: '100%',
        animation: 'fadeInUp 0.5s ease-out'
    },
    loadingCard: {
        background: 'rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        padding: '60px',
        textAlign: 'center'
    },
    loadingTitle: {
        fontSize: '32px',
        fontWeight: '700',
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        marginBottom: '30px'
    },
    spinner: {
        width: '60px',
        height: '60px',
        border: '3px solid rgba(99, 102, 241, 0.1)',
        borderTop: '3px solid #6366f1',
        borderRadius: '50%',
        margin: '0 auto',
        animation: 'spin 1s linear infinite'
    },
    errorCard: {
        background: 'rgba(239, 68, 68, 0.1)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        padding: '40px',
        textAlign: 'center'
    },
    errorTitle: {
        fontSize: '28px',
        fontWeight: '600',
        color: '#ef4444',
        marginBottom: '20px'
    },
    errorMessage: {
        fontSize: '16px',
        color: '#fca5a5',
        lineHeight: '1.6'
    }
};

const ShareV2 = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [vipConfig, setVipConfig] = useState(() => {
        const savedData = sessionStorage.getItem("VIPConfig");
        try {
            return savedData ? JSON.parse(savedData) : null;
        } catch (error) {
            console.error("Error parsing VIPConfig from sessionStorage:", error);
            return null;
        }
    });
    const [label, setLabel] = useState('');
    const [token, setToken] = useState(null);
    const location = useLocation();
    const [synthId, setSynthId] = useState(null);
    const [synthConversationId, setSynthConversationId] = useState(null);

    useEffect(() => {
        sessionStorage.removeItem("VIPConfig");
    }, [location.search, location.hash])

    useEffect(() => {
        const searchParams = getUrlParams(location);
        const tokenParam = searchParams.get('token');
        const synthIdParam = searchParams.get('synth_id');
        const conversationIdParam = searchParams.get('id');
        
        console.log('ShareV2 URL parsing:', {
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
            tokenParam,
            synthIdParam,
            conversationIdParam
        });

        if (tokenParam && tokenParam !== token) {
            setToken(tokenParam);
            sessionStorage.setItem('token', tokenParam);
        }
        else if (!tokenParam) {
            const storedToken = sessionStorage.getItem('token');
            if (storedToken) {
                setToken(storedToken);
            } else {
                // No token provided, redirect to yobi.com
                window.location.href = 'https://yobi.com';
                return;
            }
        }

        if (synthIdParam) {
            setSynthId(synthIdParam);
        }
        
        if (conversationIdParam) {
            setSynthConversationId(conversationIdParam);
            sessionStorage.setItem('synth_conversation_id', conversationIdParam);
        }
    }, [location.search, location.hash, token]);

    const fetchVIPDataByToken = async () => {
        try {
            const token = sessionStorage.getItem('token');
            const url = `${process.env.REACT_APP_API_URL}/get_vip_config_by_token/${token}`;
            console.log('Fetching VIP data by token:', { token, url });
            
            const response = await fetch(url, {
                method: 'GET',
                headers: getCorsHeaders(),
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();
            if (Array.isArray(data) && data[1] === 404) {
                throw new Error(data[0].message);
            }
            setVipConfig(data);
            
            return data;
        } catch (error) {
            console.error('Error fetching VIP data by token:', error);
            throw error;
        }
    };

    const fetchVIPData = useCallback(async () => {
        console.log('Fetching Data ...');
        setIsLoading(true);
        const tokenParam = sessionStorage.getItem('token')
        
        if (!vipConfig && tokenParam) {
            try {
                const data = await fetchVIPDataByToken();
                if (data) {
                    sessionStorage.setItem("VIPConfig", JSON.stringify(data));
                }
            } catch (error) {
                console.error('Error in fetching');
                setVipConfig(null);
            } finally {
                setIsLoading(false);
            }
        }
        else{
           setIsLoading(false)
        }
    }, [vipConfig]);

    useEffect(() => {
        const storedToken = sessionStorage.getItem('token');
        console.log('ShareV2 useEffect check:', { storedToken });
        
        if (storedToken) {
            fetchVIPData();
        } else {
            setIsLoading(false);
        }
    }, [fetchVIPData]);

    useEffect(() => {
        const searchParams = getUrlParams(location);
        const labelParam = searchParams.get('label');
        if (labelParam) {
            setLabel(labelParam);
        }
    }, [location.search, location.hash]);

    useEffect(() => {
        ReactGA.send({
            hitType: "pageview",
            page: "/v2",
            title: "V2 Share Page"
        });
    }, []);

    // Add keyframes for animations
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
            }
            @keyframes fadeInUp {
                from {
                    opacity: 0;
                    transform: translateY(30px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }
            @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.5; }
            }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    if (isLoading) {
        return (
            <div style={styles.container}>
                <div style={styles.overlay}></div>
                <div style={styles.content}>
                    <div style={styles.loadingCard}>
                        <h1 style={styles.loadingTitle}>Loading Agent...</h1>
                        <div style={styles.spinner}></div>
                    </div>
                </div>
            </div>
        );
    }

    if (!vipConfig) {
        return (
            <div style={styles.container}>
                <div style={styles.overlay}></div>
                <div style={styles.content}>
                    <div style={styles.errorCard}>
                        <h1 style={styles.errorTitle}>Agent Not Found</h1>
                        <p style={styles.errorMessage}>
                            The requested AI agent could not be found or loaded.
                            Please check the URL and try again.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.overlay}></div>
            <div style={styles.content}>
                <div style={styles.glassCard}>
                    <ShareTestCallCardStyled
                        vipConfig={vipConfig}
                        label={label}
                        token={sessionStorage.getItem('token')}
                        synthId={synthId || synthConversationId}
                    />
                </div>
            </div>
        </div>
    );
};

export default ShareV2;